import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { encodeDate } from '@campfire/hot-date';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Checkbox, MenuItem, Radio, TextField, Typography } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import { string as YupString, array as YupArray } from 'yup';
import { FormikErrorFocus } from '../../common/form/FormikErrorFocus';
import { volunteerProfileHardCodedFields } from '../../common/form/task-form/model/get-volunteer-profile-hardcoded-field-values';
import { initTaskFormFormikValues, TaskFormValuesType } from '../../common/form/task-form/model/task-form-init';
import {
  dateValidator,
  dynamicValidationSchema,
  phoneNumberValidator,
} from '../../common/form/task-form/model/validation-schema';
import { TaskFormItem } from '../../common/form/task-form/model/__generated__/TaskFormItem';
import { TaskFormFieldList } from '../../common/form/task-form/TaskFormFieldList';
import {
  DateTaskFormikField,
  PhoneTaskFormikField,
  ShortTextTaskFormikField,
} from '../../common/inputs/task-fields/TaskFormikFields';
import { TaskFieldContainer } from '../../common/inputs/containers/TaskFieldContainer';
import { convertToEditorState } from '../../screens/general/activities-v2/AllPrograms/AllProgramsHelpers';
import { deserializeTaskItems } from '../../screens/admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { useUser } from '../auth/useUser';
import { useSnackbar } from '../config/useSnackbar';
import { useEndpointFetch } from '../network/useEndpointFetch';
import { getTaskFieldValues, TaskFieldValueType } from './model/suyp-task-submission-handler';
import { SUYPTaskScaffold } from './SUYPTaskScaffold';

const buildValidationSchema = (programLabel: string, allowMulti: boolean) => ({
  firstName: YupString().required('Required'),
  lastName: YupString().required('Required'),
  preferredName: YupString().nullable(),
  dob: dateValidator(false),
  contactNumber: phoneNumberValidator(false),
  programIds: YupArray()
    .of(YupString())
    .min(
      1,
      allowMulti
        ? `Please select at least one ${programLabel.toLowerCase()}`
        : `Please select a ${programLabel.toLowerCase()}`
    ),
  taskId: YupString().required('Required'),
});

type VolProfileInitialValues = {
  firstName: string;
  lastName: string;
  preferredName: string;
  dob: null | DateTime;
  contactNumber: string;
  programIds: string[];
  [key: string]: any;
};

type VolunteerProfileSubmission = {
  firstName: string;
  lastName: string;
  preferredName: string;
  dob: string;
  contactNumber: string;
  programIds: string[];
  taskId: string;
  taskFieldValues: TaskFieldValueType[];
  userId: string;
};

interface FunnelProgramData {
  lockPrograms: boolean;
  programIds: string[];
}

interface SUYPVolunteerProfileProps {
  task?: {
    taskId: string;
  };
  taskItems: TaskFormItem[];
  programs: {
    programId: string;
    name: string;
    description: string | null;
    dateSuspended: any | null;
  }[];
  refetch?: () => void;
  loading: boolean;
  allowMultiProgramApplication?: boolean;
  funnelData?: FunnelProgramData;
  programLabel?: string;
}

export const SUYPVolunteerProfile = (props: SUYPVolunteerProfileProps) => {
  const {
    taskItems,
    task,
    refetch,
    loading,
    programs,
    allowMultiProgramApplication = false,
    funnelData,
    programLabel = 'Program',
  } = props;
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();

  const submitVolunteerProfile = useEndpointFetch<VolunteerProfileSubmission>(
    '/vm/volunteer/profile/completed-task/save-with-profile'
  );

  const initialFormikValues = useMemo(() => {
    return {
      ...initTaskFormFormikValues(taskItems, undefined),
      taskId: task?.taskId,
      firstName: user.firstName,
      lastName: user.lastName,
      preferredName: '',
      dob: null,
      contactNumber: '',
      programIds: funnelData?.programIds ?? ([] as string[]),
    };
  }, [taskItems, funnelData]);

  const parsedTaskItems = deserializeTaskItems(taskItems);

  const baseValidation = buildValidationSchema(programLabel, allowMultiProgramApplication);
  const validationSchema = dynamicValidationSchema(parsedTaskItems, baseValidation, funnelData?.programIds);

  const handleSubmit = async (values: VolProfileInitialValues) => {
    const taskFieldValues = await getTaskFieldValues(parsedTaskItems, values);

    submitVolunteerProfile
      .run({
        ...values,
        dob: encodeDate(values.dob as DateTime),
        preferredName: values.preferredName !== '' ? values.preferredName : values.firstName,
        taskFieldValues,
        taskId: task?.taskId ?? '',
        userId: user.userId,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to save volunteer profile',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Volunteer profile saved',
          variant: 'success',
        });
        if (refetch) refetch();
      })
      .catch(() => {});
  };

  const orderedPrograms = useMemo(() => {
    let filtered = programs.filter((program) => program.dateSuspended === null);
    if (funnelData && funnelData.programIds.length > 0) {
      filtered = filtered.filter((p) => funnelData.programIds.includes(p.programId));
    }
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [programs, funnelData]);

  return loading ? (
    <CircularProgressOverlay isLoading />
  ) : (
    <Box bgcolor='#f1f1f1' display='flex' flex='1 1 auto' flexDirection='column' width={1} paddingTop={2}>
      <LinearProgressOverlay isLoading={submitVolunteerProfile.isLoading} />

      <SUYPTaskScaffold
        allowBack={false}
        taskHeaderDetails={{ title: `Hey, ${user.preferredName}`, description: `Let's get your profile started` }}
      >
        <Formik initialValues={initialFormikValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ values: fullValues, setFieldValue, errors, touched }) => {
            const {
              taskId: _unusedTaskId,
              firstName: _unusedFirstTime,
              lastName: _unusedLastTime,
              preferredName: _unusedPreferredName,
              dob: _unusedDOB,
              contactNumber: _unusedContactNumber,
              programIds,
              ...values
            } = fullValues;

            return (
              <Form>
                <Box display='flex' justifyContent='center' flex='1 1 auto'>
                  <Box maxWidth={720} width={1}>
                    <ShortTextTaskFormikField
                      name='firstName'
                      ariaId='firstName'
                      title={volunteerProfileHardCodedFields.firstName.name}
                      ariaDescribedBy='First name'
                      required
                      outlinedInputProps={{ autoFocus: true }}
                      autoComplete='no'
                    />
                    <ShortTextTaskFormikField
                      name='lastName'
                      ariaId='lastName'
                      title={volunteerProfileHardCodedFields.lastName.name}
                      ariaDescribedBy='Last Name'
                      required
                      autoComplete='no'
                    />
                    <ShortTextTaskFormikField
                      name='preferredName'
                      ariaId='preferredName'
                      title={volunteerProfileHardCodedFields.preferredName.name}
                      description={volunteerProfileHardCodedFields.preferredName.description}
                      ariaDescribedBy='Preferred Name'
                      autoComplete='no'
                    />
                    <DateTaskFormikField
                      name='dob'
                      ariaId='dob'
                      title={volunteerProfileHardCodedFields.dob.name}
                      ariaDescribedBy='Date of Birth'
                    />
                    <PhoneTaskFormikField
                      name='contactNumber'
                      ariaId='contactNumber'
                      title={volunteerProfileHardCodedFields.contactNumber.name}
                      description={volunteerProfileHardCodedFields.contactNumber.description}
                      ariaDescribedBy='Phone number'
                      required
                    />
                    <TaskFieldContainer
                      title={`Choose a ${programLabel}`}
                      description={`Select a ${programLabel.toLowerCase()} to start with`}
                      ariaId='programIds'
                      ariaDescribedBy={programLabel}
                      required
                      error={touched.programIds && Boolean(errors.programIds)}
                      helperText={touched.programIds && errors.programIds ? String(errors.programIds) : undefined}
                    >
                      {allowMultiProgramApplication ? (
                        <TextField
                          fullWidth
                          variant='outlined'
                          select
                          disabled={funnelData?.lockPrograms}
                          value={programIds}
                          onChange={(e) => setFieldValue('programIds', e.target.value)}
                          style={{ maxWidth: 540 }}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            MenuProps: {
                              PaperProps: {
                                style: { maxWidth: 540 },
                              },
                            },
                            renderValue: (selected) => {
                              const selectedArr = selected as string[];
                              if (!selectedArr || selectedArr.length === 0) {
                                return (
                                  <span style={{ color: '#9e9e9e' }}>{`Select ${pluralize(
                                    programLabel
                                  ).toLowerCase()}`}</span>
                                );
                              }
                              return orderedPrograms
                                .filter((p) => selectedArr.includes(p.programId))
                                .map((p) => p.name)
                                .join(', ');
                            },
                          }}
                        >
                          {orderedPrograms.map((program) => (
                            <MenuItem
                              key={program.programId}
                              value={program.programId}
                              style={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
                            >
                              <Checkbox
                                color='primary'
                                checked={programIds.includes(program.programId)}
                                style={{ marginRight: 8, padding: 4 }}
                              />
                              <Box>
                                <Typography variant='body1'>{program.name}</Typography>
                                {program.description && (
                                  <Box maxHeight={60} overflow='hidden'>
                                    <Typography
                                      variant='body2'
                                      color='textSecondary'
                                      component='div'
                                      style={{ fontSize: '0.8rem' }}
                                    >
                                      <CampfireRichEditor
                                        editorState={convertToEditorState(program.description)}
                                        readOnly
                                      />
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          fullWidth
                          variant='outlined'
                          select
                          disabled={funnelData?.lockPrograms}
                          value={programIds[0] || ''}
                          onChange={(e) => setFieldValue('programIds', e.target.value ? [e.target.value] : [])}
                          style={{ maxWidth: 540 }}
                          SelectProps={{
                            displayEmpty: true,
                            MenuProps: {
                              PaperProps: {
                                style: { maxWidth: 540 },
                              },
                            },
                            renderValue: (selected) => {
                              if (!selected) {
                                return (
                                  <span style={{ color: '#9e9e9e' }}>{`Select a ${programLabel.toLowerCase()}`}</span>
                                );
                              }
                              const selectedProgram = orderedPrograms.find((p) => p.programId === selected);
                              return selectedProgram?.name || '';
                            },
                          }}
                        >
                          {orderedPrograms.map((program) => (
                            <MenuItem
                              key={program.programId}
                              value={program.programId}
                              style={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
                            >
                              <Radio
                                color='primary'
                                checked={programIds[0] === program.programId}
                                style={{ marginRight: 8, padding: 4 }}
                              />
                              <Box>
                                <Typography variant='body1'>{program.name}</Typography>
                                {program.description && (
                                  <Box maxHeight={60} overflow='hidden'>
                                    <Typography
                                      variant='body2'
                                      color='textSecondary'
                                      component='div'
                                      style={{ fontSize: '0.8rem' }}
                                    >
                                      <CampfireRichEditor
                                        editorState={convertToEditorState(program.description)}
                                        readOnly
                                      />
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TaskFieldContainer>

                    <TaskFormFieldList
                      taskItems={deserializeTaskItems(taskItems)}
                      disableAutoFocus
                      values={values as TaskFormValuesType}
                      volunteerProgramIds={programIds}
                    />

                    <TabletButton
                      aria-label='submit'
                      color='primary'
                      variant='contained'
                      type='submit'
                      size='large'
                      style={{ width: 256 }}
                    >
                      Submit
                    </TabletButton>
                  </Box>
                </Box>
                <FormikErrorFocus />
              </Form>
            );
          }}
        </Formik>
      </SUYPTaskScaffold>
    </Box>
  );
};
